import {
  getValidationCodeDescription,
  isLookupRequiredCode,
} from "./ContextImportValidationCodes";
import { buildNodeLookup, resolveNodeDisplayName } from "./ContextImportNodeLookup";

export const VALIDATION_VERDICT = {
  VALID: "valid",
  FAILED: "failed",
  REFUSED: "refused",
};

function readMetric(data, camelKey, snakeKey) {
  const value = data?.[camelKey] ?? data?.[snakeKey];
  return Number(value) || 0;
}

function normalizeError(raw) {
  const nodeId = raw?.nodeId ?? raw?.node_id ?? null;
  return {
    nodeId: nodeId != null ? String(nodeId) : null,
    field: raw?.field ?? "",
    code: String(raw?.code ?? "").trim(),
    message: typeof raw?.message === "string" ? raw.message : "",
  };
}

export function formatCount(value) {
  return Number(value).toLocaleString("en-US");
}

export function buildRunSummaryLine(validationResult, phase = "validation") {
  if (validationResult.refusedReason) {
    return validationResult.refusedReason;
  }
  const { totalNodes, createdNodes, updatedNodes, failedNodes, verdict } =
    validationResult;
  if (verdict === VALIDATION_VERDICT.VALID) {
    if (phase === "complete") {
      return `${formatCount(totalNodes)} nodes · ${formatCount(createdNodes)} created · ${formatCount(updatedNodes)} updated`;
    }
    return `${formatCount(totalNodes)} nodes · ${formatCount(createdNodes)} to create · ${formatCount(updatedNodes)} to update`;
  }
  return `${formatCount(totalNodes)} nodes · ${formatCount(failedNodes)} failed`;
}

export function buildValidAnnouncement(fileName, validationResult) {
  const { totalNodes, createdNodes, updatedNodes } = validationResult;
  return `${fileName} is valid: ${formatCount(totalNodes)} nodes, ${formatCount(createdNodes)} to create, ${formatCount(updatedNodes)} to update. Ready to upload.`;
}

export function buildCompleteAnnouncement(fileName, validationResult) {
  const { totalNodes, createdNodes, updatedNodes } = validationResult;
  return `${fileName} is imported: ${formatCount(totalNodes)} nodes, ${formatCount(createdNodes)} created, ${formatCount(updatedNodes)} updated.`;
}

function buildCodeLegend(errors) {
  const byCode = new Map();
  errors.forEach((issue, index) => {
    if (!issue.code) {
      return;
    }
    if (!byCode.has(issue.code)) {
      byCode.set(issue.code, {
        code: issue.code,
        nodeKeys: new Set(),
        sampleMessage: issue.message,
      });
    }
    const entry = byCode.get(issue.code);
    entry.nodeKeys.add(issue.nodeId ?? `__row-${index}`);
    if (!entry.sampleMessage && issue.message) {
      entry.sampleMessage = issue.message;
    }
  });

  return [...byCode.values()]
    .map((entry) => ({
      code: entry.code,
      count: entry.nodeKeys.size,
      description: getValidationCodeDescription(entry.code, entry.sampleMessage),
      lookupRequired: isLookupRequiredCode(entry.code),
    }))
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
}

export function buildValidationResult({
  apiData,
  uploadedNodes = [],
  fileName = "",
  refusedReason = null,
} = {}) {
  if (refusedReason) {
    return {
      fileName,
      verdict: VALIDATION_VERDICT.REFUSED,
      canUpload: false,
      totalNodes: 0,
      createdNodes: 0,
      updatedNodes: 0,
      failedNodes: 0,
      passedCount: 0,
      resultsText: refusedReason,
      errors: [],
      codeLegend: [],
      refusedReason,
      uploadedNodes,
    };
  }

  const errors = (apiData?.errors ?? []).map(normalizeError);
  const totalNodes = readMetric(apiData, "totalNodes", "total_nodes");
  const createdNodes = readMetric(apiData, "createdNodes", "created_nodes");
  const updatedNodes = readMetric(apiData, "updatedNodes", "updated_nodes");
  const failedNodes = readMetric(apiData, "failedNodes", "failed_nodes");
  const passedCount = Math.max(0, totalNodes - failedNodes);

  const hasBlockingFailure =
    errors.length > 0 || failedNodes > 0 || apiData?.isValid === false;
  const verdict = hasBlockingFailure
    ? VALIDATION_VERDICT.FAILED
    : VALIDATION_VERDICT.VALID;

  const resultsText = hasBlockingFailure
    ? `${formatCount(passedCount)} of ${formatCount(totalNodes)} nodes passed validation. Nothing was imported: Created and Updated show what the passing nodes will write once every node passes.`
    : buildValidAnnouncement(fileName, {
        totalNodes,
        createdNodes,
        updatedNodes,
      });

  return {
    fileName,
    verdict,
    canUpload: verdict === VALIDATION_VERDICT.VALID,
    totalNodes,
    createdNodes,
    updatedNodes,
    failedNodes,
    passedCount,
    resultsText,
    errors,
    codeLegend: buildCodeLegend(errors),
    refusedReason: null,
    uploadedNodes,
  };
}

export function resolveErrorNodeName(error, nodeLookup) {
  if (!error?.nodeId) {
    return "";
  }
  const node = nodeLookup.get(String(error.nodeId));
  return resolveNodeDisplayName(node, error.nodeId);
}

export function buildNodeLookupFromUploadedNodes(uploadedNodes) {
  return buildNodeLookup(uploadedNodes);
}
